import {
  Box,
  CircularProgress,
  LinearProgress,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import React, { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { studentService } from "../../services/student.service";
import Layout from "../../shared/Layout";
import { Modal, ThemeModal } from "../../shared/ui/Modal/Modal";
import { NextWindowContext } from "../LearnDashboardPreview/context/NextWindowContext";
import { ArticleComponent } from "../LearnDashboardPreview/ui/LearnDashboardView/ArticleComponent/ArticleComponent";
import { SlidesComponent } from "../LearnDashboardPreview/ui/LearnDashboardView/SlidesComponent/SlidesComponent";
import { TaskComponent } from "../LearnDashboardPreview/ui/LearnDashboardView/TaskComponent/TaskComponent";
import { TestComponent } from "../LearnDashboardPreview/ui/LearnDashboardView/TestComponent/TestComponent";
import { ReactPlayerComponent } from "../LearnDashboardPreview/ui/ReactPlayer/ReactPlayerComponent";
import cls from "./LearnPage.module.scss";
import { LearnPageNavmenu } from "./ui/LearnPageNavmenu/LearnPageNavmenu";
import { LearnPageSidebar } from "./ui/LearnPageSidebar/LearnPageSidebar";
import Header from "../../shared/Header";

const useFetchStudentIdCourse = (studentId: string) => {
  return useQuery<any | undefined, Error>({
    queryKey: ["courseInfo", studentId],
    queryFn: () => studentService.getStudentIdCourse(studentId),
  });
};

interface ContentWindowParams {
  contentType: "article" | "video" | "slide" | "test" | "task";
  contentData: any;
}

export const LearnPage = () => {
  const isMobile = useMediaQuery("(max-width:1000px)");
  const { studentId } = useParams();
  const [collapsed, setCollapsed] = useState(false);
  const [passedPart, setPassedPart] = useState<any>([]);
  const [isAuthModal, setIsAuthModal] = useState(false);
  const [isAuthModalError, setIsAuthModalError] = useState(false);
  const [isFinished, setIsFinished] = useState(false);
  const [activeSection, setActiveSection] = useState<number | null>(null);
  const [activeLesson, setActiveLesson] = useState<string | null>(null);
  const [lastLesson, setLastLesson] = useState(false);
  const {
    data: studentCourse,
    isLoading: isLoadingStudentIdCourse,
    isError: isErrorStudentIdCourse,
    error: errorStudentIdCourse,
  } = useFetchStudentIdCourse(studentId || "");

  const onToggleModal = useCallback(() => {
    setIsAuthModal((prev) => !prev);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const onToggleModalError = useCallback(() => {
    setIsAuthModalError((prev) => !prev);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [contentWindow, setContentWindow] =
    useState<ContentWindowParams | null>(null);

  const getStorageKey = useCallback(
    () => `lastOpenedContent-${studentId}`,
    [studentId]
  );

  useEffect(() => {
    const storageKey = getStorageKey();
    const savedContent = localStorage.getItem(storageKey);
    if (savedContent) {
      const lastContentData = JSON.parse(savedContent);
      setContentWindow(lastContentData);
    }
  }, [getStorageKey]);

  const handleOpenContentWindow = useCallback(
    (
      contentType: "article" | "video" | "slide" | "test" | "task",
      contentData: any
    ) => {
      setContentWindow({ contentType, contentData });
      const storageKey = getStorageKey();
      localStorage.setItem(
        storageKey,
        JSON.stringify({ contentType, contentData })
      );
    },
    [getStorageKey]
  );
  const checkIfAllLessonsCompletedExceptLast = () => {
    let allExceptLastCompleted = false;

    if (studentCourse?.data?.parts) {
      const lastPartWithLessons = studentCourse.data.parts
        .slice()
        .reverse() // Меняем порядок на обратный, чтобы начать с последнего раздела
        .find((part: any) => part.items && part.items.length > 0);

      if (lastPartWithLessons) {
        const lastLessonIndex = lastPartWithLessons.items.length - 1;
        allExceptLastCompleted = studentCourse.data.parts.every(
          (part: { items: any[] }, index: string | number) => {
            if (studentCourse.data.parts[index].id !== lastPartWithLessons.id) {
              return part.items.every((lesson) => lesson.completed);
            }
            return part.items
              .slice(0, lastLessonIndex)
              .every((lesson) => lesson.completed);
          }
        );
      }
    }

    setIsFinished(allExceptLastCompleted);
  };

  useEffect(() => {
    checkIfAllLessonsCompletedExceptLast();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studentCourse]);
  useEffect(() => {
    const storageKey = getStorageKey();
    const lastOpenedContent = JSON.parse(
      localStorage.getItem(storageKey) || "{}"
    );
    if (lastOpenedContent && lastOpenedContent.contentType) {
      handleOpenContentWindow(
        lastOpenedContent.contentType,
        lastOpenedContent.contentData
      );
    }
  }, [handleOpenContentWindow, getStorageKey]);

  const handlePutPassedLesson = useCallback(
    async (item: any) => {
      console.log("ID Урока", item.id);
      try {
        await studentService.completeLesson(studentId || "", item.id);
        console.log("Урок пройден");

        setPassedPart((prevState: any[]) => {
          const isLessonAlreadyCompleted = prevState.some(
            (lesson: any) => lesson.id === item.id
          );

          if (!isLessonAlreadyCompleted) {
            return [...prevState, { ...item, completed: true }];
          } else {
            return prevState;
          }
        });
      } catch (e) {
        console.error(e);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [studentId]
  );

  useEffect(() => {
    if (
      studentCourse?.data?.parts?.length &&
      studentCourse.data.parts[studentCourse.data.parts.length - 1]?.items
        ?.length
    ) {
      const lastPart =
        studentCourse.data.parts[studentCourse.data.parts.length - 1];
      const lastLesson = lastPart.items[lastPart.items.length - 1];

      if (contentWindow?.contentData) {
        setLastLesson(contentWindow.contentData.id === lastLesson.id);
      }
    } else {
      setLastLesson(false);
    }
  }, [studentCourse, contentWindow]);
  const handlePutPassedPart = useCallback(
    async (partId: any) => {
      console.log("ID Раздела", partId);
      try {
        await studentService.completeLesson(studentId || "", partId);
        console.log("Раздел пройден");

        setPassedPart((prevState: any[]) => {
          return prevState;
        });
      } catch (e) {
        console.error(e);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [studentId]
  );

  const nextWindow = useCallback(() => {
    if (!contentWindow) return;

    const allItemsWithPartIndex = studentCourse?.data?.parts.flatMap(
      (part: { items: any[] }, index: any) =>
        part.items.map((item: any) => ({ ...item, partIndex: index }))
    );

    const partNames = new Map();
    studentCourse?.data?.parts.forEach(
      (part: { items: any[]; id: any }, index: any) => {
        part.items.forEach((item) =>
          partNames.set(item.id, { partId: part.id, partIndex: index })
        );
      }
    );

    const currentId = allItemsWithPartIndex.findIndex(
      (item: { [x: string]: unknown }) =>
        Object.entries(contentWindow.contentData).every(
          ([key, value]) => item[key] === value
        )
    );

    if (currentId === -1) return;

    const processItem = (
      item: { id: React.SetStateAction<string | null> },
      isNext = false
    ) => {
      const contentType = getContentType(item);
      handleOpenContentWindow(contentType, item);
      handlePutPassedLesson(allItemsWithPartIndex[currentId]);

      const partInfo = partNames.get(item.id);
      const isLastLessonInPart =
        studentCourse.data.parts[partInfo.partIndex].items.slice(-1)[0].id ===
        item.id;

      console.log("Это последний урок в разделе", isLastLessonInPart);
      if (isLastLessonInPart) {
        console.log(
          `Раздел "${
            studentCourse.data.parts[partInfo.partIndex].name
          }" завершен`
        );
        handlePutPassedPart(partInfo.partId);
      }

      if (currentId >= allItemsWithPartIndex.length - 2) {
        const allExceptLastCompleted = checkIfAllLessonsCompletedExceptLast();
        if (allExceptLastCompleted) {
          setIsFinished(true);
          onToggleModal();
        } else {
          onToggleModalError();
        }
      }

      if (isNext) {
        setActiveSection(partInfo.partIndex);
        setActiveLesson(item.id);
      }
    };
    const checkIfAllLessonsCompletedExceptLast = () => {
      return isFinished;
    };

    const getContentType = (item: {
      id?: React.SetStateAction<string | null>;
      video?: any;
      video_name?: any;
      video_extension?: any;
      slides?: any;
      slides_file_extension?: any;
      slides_file_name?: any;
      test_name?: any;
      questions?: any;
      exercise_name?: any;
      instructions?: any;
    }) => {
      if (item.video && item.video_name && item.video_extension) return "video";
      if (item.slides && item.slides_file_extension && item.slides_file_name)
        return "slide";
      if (item.test_name && item.questions) return "test";
      if (item.exercise_name && item.instructions) return "task";
      return "article";
    };

    const item =
      allItemsWithPartIndex[
        currentId + (currentId >= allItemsWithPartIndex.length - 1 ? 0 : 1)
      ];
    processItem(item, currentId < allItemsWithPartIndex.length - 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    contentWindow,
    studentCourse,
    handlePutPassedPart,
    handlePutPassedLesson,
    onToggleModal,
    setActiveSection,
    setActiveLesson,
  ]);
  const renderContentWindow = (
    contentWindowParams: ContentWindowParams | null
  ) => {
    if (!contentWindowParams) return null;

    switch (contentWindowParams.contentType) {
      case "article":
        return (
          <ArticleComponent
            data={contentWindowParams.contentData}
            isFinished={isFinished}
            lastLesson={lastLesson}
          />
        );
      case "video":
        return (
          <ReactPlayerComponent
            data={contentWindowParams.contentData}
            setCollapsed={setCollapsed}
            collapsed={collapsed}
            showNext={true}
            isFinished={isFinished}
            lastLesson={lastLesson}
          />
        );
      case "slide":
        return (
          <SlidesComponent
            data={contentWindowParams.contentData}
            isFinished={isFinished}
            lastLesson={lastLesson}
          />
        );
      case "test":
        return (
          <TestComponent
            data={contentWindowParams.contentData}
            isFinished={isFinished}
            lastLesson={lastLesson}
          />
        );
      case "task":
        return (
          <TaskComponent
            setCollapsed={setCollapsed}
            collapsed={collapsed}
            data={contentWindowParams.contentData}
            isFinished={isFinished}
            lastLesson={lastLesson}
          />
        );
      default:
        return <Box>Тип контента не поддерживается</Box>;
    }
  };
  if (isLoadingStudentIdCourse) {
    return (
      <Box>
        <LinearProgress />
      </Box>
    );
  }
  if (isErrorStudentIdCourse) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100%",
        }}>
        <Typography>
          Ошибка:{" "}
          {errorStudentIdCourse?.message || errorStudentIdCourse?.message}
        </Typography>
      </Box>
    );
  }
  if (errorStudentIdCourse) {
    return (
      <Layout>
        <Box>Курс не найден</Box>
      </Layout>
    );
  }

  return (
    <Box
      className={"studyDashboard"}
      sx={{
        display: "flex",
        flexDirection: "column",
      }}>
      <Modal
        theme={ThemeModal.PRIMARY}
        isOpen={isAuthModal}
        onClose={onToggleModal}>
        <Typography fontSize={20}>Вы прошли курс!</Typography>
      </Modal>

      <Modal
        theme={ThemeModal.PRIMARY}
        isOpen={isAuthModalError}
        onClose={onToggleModalError}>
        <Typography fontSize={20}>
          Не все уроки пройдены, курс не будет отмечен как завершенный.
        </Typography>
      </Modal>

      {!isMobile && (
        <LearnPageNavmenu
          studentId={studentId}
          studentCourse={studentCourse?.data}
        />
      )}

      {isMobile && <Header />}

      <div className={cls.content}>
        <LearnPageSidebar
          studentId={studentId}
          setCollapsed={setCollapsed}
          collapsed={collapsed}
          studentCourse={studentCourse?.data}
          onItemSelect={handleOpenContentWindow}
          passedPart={passedPart}
          activeSection={activeSection}
          activeLesson={activeLesson}
        />
        {isLoadingStudentIdCourse ? (
          <Box
            sx={{
              width: "100%",
              height: "100%",
              padding: "20px",
              overflow: "auto",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              paddingTop: "35vh",
            }}>
            <CircularProgress />
          </Box>
        ) : (
          <NextWindowContext.Provider value={nextWindow}>
            {renderContentWindow(contentWindow)}
          </NextWindowContext.Provider>
        )}
      </div>
    </Box>
  );
};
